import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { conditionalPersistStorage } from '@/stores/persistStorage';
import { uuid } from '@/lib/uuid';
import { useUserPatternStore } from '@/stores/userPatternStore';
import { useAuthStore } from '@/stores/authStore';
import { writeTask, deleteTask as fsDeleteTask } from '@/stores/sync';
import { toDateKey } from '@/lib/dateKey';
import type { MicroStep, SkipReason, Subtask, Task, TaskStatus } from '@/types';

function syncTask(task: Task) {
  const { uid, authMode } = useAuthStore.getState();
  if (authMode === 'authenticated' && uid) {
    void writeTask(uid, task);
  }
}

function syncDeleteTask(taskId: string) {
  const { uid, authMode } = useAuthStore.getState();
  if (authMode === 'authenticated' && uid) {
    void fsDeleteTask(uid, taskId);
  }
}

export interface TaskState {
  tasks: Task[];
  addTask: (
    input: Omit<Task, 'id' | 'createdAt' | 'updatedAt' | 'subtasks' | 'tags' | 'status'> &
      Partial<Pick<Task, 'subtasks' | 'tags' | 'status'>>,
  ) => Task;
  updateTask: (
    id: string,
    patch: { [K in keyof Omit<Task, 'id' | 'createdAt'>]?: Task[K] | undefined },
  ) => void;
  removeTask: (id: string) => void;
  restoreTask: (task: Task) => void;
  setStatus: (id: string, status: TaskStatus) => void;
  completeTask: (id: string) => void;
  skipTask: (id: string, reason: SkipReason) => void;
  addSubtask: (taskId: string, title: string) => void;
  toggleSubtask: (taskId: string, subtaskId: string) => void;
  scheduleTask: (id: string, start: Date, end: Date) => void;
  setMicroStep: (id: string, microStep: MicroStep) => void;
}

export const useTaskStore = create<TaskState>()(
  persist(
    (set, get) => ({
      tasks: [],
      addTask: (input) => {
        const now = new Date();
        const task: Task = {
          subtasks: [],
          tags: [],
          status: 'inbox',
          ...input,
          id: uuid(),
          createdAt: now,
          updatedAt: now,
        };
        set((s) => ({ tasks: [...s.tasks, task] }));
        syncTask(task);
        return task;
      },
      updateTask: (id, patch) => {
        set((s) => ({
          tasks: s.tasks.map((t) => {
            if (t.id !== id) return t;
            const next: Task = { ...t, updatedAt: new Date() };
            for (const key of Object.keys(patch) as (keyof typeof patch)[]) {
              if (patch[key] === undefined) {
                delete next[key as keyof Task];
              } else {
                // Safe: key is a valid Task key and value is defined
                (next as unknown as Record<string, unknown>)[key] = patch[key];
              }
            }
            return next;
          }),
        }));
        const updated = get().tasks.find((t) => t.id === id);
        if (updated) syncTask(updated);
      },
      removeTask: (id) => {
        set((s) => ({ tasks: s.tasks.filter((t) => t.id !== id) }));
        syncDeleteTask(id);
      },
      // Re-insert a previously removed task verbatim (preserves id, schedule,
      // timestamps) so a delete can be undone. No-op if the id is already
      // present, so a stale undo can't create a duplicate.
      restoreTask: (task) => {
        if (get().tasks.some((t) => t.id === task.id)) return;
        set((s) => ({ tasks: [...s.tasks, task] }));
        syncTask(task);
      },
      setStatus: (id, status) => {
        set((s) => ({
          tasks: s.tasks.map((t) => (t.id === id ? { ...t, status, updatedAt: new Date() } : t)),
        }));
        const updated = get().tasks.find((t) => t.id === id);
        if (updated) syncTask(updated);
      },
      completeTask: (id) => {
        set((s) => {
          const next = s.tasks.map((t) => {
            if (t.id !== id) return t;
            const now = new Date();
            useUserPatternStore.getState().recordCompletion({
              hour: now.getHours(),
              date: toDateKey(now),
            });
            return { ...t, status: 'done' as const, completedAt: now, updatedAt: now };
          });
          return { tasks: next };
        });
        const updated = get().tasks.find((t) => t.id === id);
        if (updated) syncTask(updated);
      },
      skipTask: (id, reason) => {
        set((s) => ({
          tasks: s.tasks.map((t) => {
            if (t.id !== id) return t;
            const now = new Date();
            return {
              ...t,
              status: 'skipped',
              skippedAt: now,
              skipReason: reason,
              updatedAt: now,
            };
          }),
        }));
        const updated = get().tasks.find((t) => t.id === id);
        if (updated) syncTask(updated);
      },
      addSubtask: (taskId, title) => {
        set((s) => ({
          tasks: s.tasks.map((t) => {
            if (t.id !== taskId) return t;
            const sub: Subtask = {
              id: uuid(),
              title,
              done: false,
              order: t.subtasks.length,
            };
            return { ...t, subtasks: [...t.subtasks, sub], updatedAt: new Date() };
          }),
        }));
        const updated = get().tasks.find((t) => t.id === taskId);
        if (updated) syncTask(updated);
      },
      toggleSubtask: (taskId, subtaskId) => {
        set((s) => ({
          tasks: s.tasks.map((t) => {
            if (t.id !== taskId) return t;
            return {
              ...t,
              subtasks: t.subtasks.map((sub) =>
                sub.id === subtaskId ? { ...sub, done: !sub.done } : sub,
              ),
              updatedAt: new Date(),
            };
          }),
        }));
        const updated = get().tasks.find((t) => t.id === taskId);
        if (updated) syncTask(updated);
      },
      scheduleTask: (id, start, end) => {
        set((s) => ({
          tasks: s.tasks.map((t) =>
            t.id === id
              ? {
                  ...t,
                  scheduledStart: start,
                  scheduledEnd: end,
                  status: 'scheduled',
                  updatedAt: new Date(),
                }
              : t,
          ),
        }));
        const updated = get().tasks.find((t) => t.id === id);
        if (updated) syncTask(updated);
      },
      setMicroStep: (id, microStep) => {
        set((s) => ({
          tasks: s.tasks.map((t) => (t.id === id ? { ...t, microStep, updatedAt: new Date() } : t)),
        }));
        const updated = get().tasks.find((t) => t.id === id);
        if (updated) syncTask(updated);
      },
    }),
    { name: 'brainday:tasks', storage: conditionalPersistStorage<TaskState>() },
  ),
);
